<template>
    <div class="w-full bg-white flex items-center justify-center lg:py-20">
        <div class="flex lg:w-8/12 w-full flex-col space-y-8 border border-neutral-300 py-10 lg:px-14 px-4">
            <img :src="companyLogo.src" class="manufacturer_canprev_logo" :alt="companyLogo.alt + ' brand logo'">
            <div>
                <h3 class="text-xl text-neutral-800 font-semibold">Manufacturer Confirmation Form</h3>
                <p class="text-base text-neutral-800 font-medium">Please enter your unique password to access the form.</p>
            </div>
            <div class="space-y-1 mb-2 ">
                <label class="flex items-center text-neutral-500 text-sm font-medium" for="password">
                    Password <span class="text-red-500 ml-1">*</span>
                </label>
                <div class="relative">
                    <input v-model="password" @keydown.enter="loginUser" class="appearance-none border rounded-md w-full py-2.5 px-3 text-neutral-800 leading-tight focus:ring-0 focus:outline-none focus:border-primary-500 placeholder:text-neutral-400" 
                        :type="showPassword ? 'text' : 'password'" placeholder="Enter Password"
                        :class="`${(v$.password.$error || error) ? 'border-red-500' : 'border-neutral-300'}`">
                    <span 
                        @click="showPassword = !showPassword" 
                        class="absolute right-3 top-3 cursor-pointer">
                        <svg v-if="showPassword" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-500 show" onclick="showHidePassword('hide')" id="hide-password" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"></path></svg>
                        <svg v-else xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-gray-500"  fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path><path stroke-linecap="round" stroke-linejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path></svg>
                    </span>
                </div>
                <div v-if="v$.password.$error"
                    class="flex items-center tracking-wide text-red-500 text-xs mt-1 ml-1">
                    <span v-for="error of v$.password.$errors"
                        :key="error.$uid">
                        {{error.$message}}
                    </span>
                </div>
                <div v-else-if="error" class="py-0.5 text-red-500 text-xs font-normal">{{ error }}</div>
            </div>
            <div class="bg-gray-100 rounded-lg p-6">
                <div @click="instructionOpen = !instructionOpen" class="flex cursor-pointer items-center justify-between">
                    <h3 class="font-semibold text-lg text-neutral-800">Steps to complete the form</h3>
                    <svg  v-if="instructionOpen" xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                        <path d="M5 15.5L12 8.5L19 15.5" stroke="#444451" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <svg v-else xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                        <path d="M19 9.5L12 16.5L5 9.5" stroke="#111827" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </div>
                <ul v-if="instructionOpen" class="list-none list-inside canprev-500 text-base font-medium">
                    <li v-for="step in steps" class="my-4">
                        <div class="flex items-center space-between space-x-2">
                            <span v-html="step.icon"></span>
                            <span :class="step.color" class="font-medium">{{ step.instruction }}</span>
                        </div>
                    </li>
                </ul>
            </div>
            <div class="flex items-center flex-row-reverse">
                <button :disabled="isSubmitted" @click="loginUser" type="submit" class="flex items-center justify-center px-5 py-1.5 rounded-md bg-primary-500 hover:shadow-md hover:bg-primary-600 text-white"> 
                    <svg v-if="isSubmitted" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Access
                </button>
            </div>
        </div>
    </div>
</template>
<script>
/* eslint-disable */
import axios from 'axios';
import { minLength, maxLength, required, helpers, numeric } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'
import mixin from '@/mixin.js'
export default {
    mixins: [mixin],
    setup() {
        return { 
            v$: useVuelidate()
        }
    },
    data() {
        return {
            steps: [
                {
                    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
                            <rect width="40" height="40" rx="4" fill="#2A439F"/>
                            <path d="M17 13H15C13.8954 13 13 13.8954 13 15V27C13 28.1046 13.8954 29 15 29H25C26.1046 29 27 28.1046 27 27V15C27 13.8954 26.1046 13 25 13H23M17 13C17 14.1046 17.8954 15 19 15H21C22.1046 15 23 14.1046 23 13M17 13C17 11.8954 17.8954 11 19 11H21C22.1046 11 23 11.8954 23 13" stroke="white" stroke-width="2" stroke-linecap="round"/>
                            </svg>`,
                    instruction: 'Enter PO number to find the associated lot(s).'
                },
                {
                    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
                        <rect width="40" height="40" rx="4" fill="#2A439F"/>
                        <rect x="10" y="12" width="20" height="16" rx="1" stroke="white" stroke-width="2"/>
                        <path d="M14.6681 22.89H16.9681V24H13.2681V17.02H14.6681V22.89ZM20.318 24.09C19.7846 24.09 19.3046 23.9733 18.878 23.74C18.4513 23.5 18.1146 23.1633 17.868 22.73C17.628 22.2967 17.508 21.7967 17.508 21.23C17.508 20.6633 17.6313 20.1633 17.878 19.73C18.1313 19.2967 18.4746 18.9633 18.908 18.73C19.3413 18.49 19.8246 18.37 20.358 18.37C20.8913 18.37 21.3746 18.49 21.808 18.73C22.2413 18.9633 22.5813 19.2967 22.828 19.73C23.0813 20.1633 23.208 20.6633 23.208 21.23C23.208 21.7967 23.078 22.2967 22.818 22.73C22.5646 23.1633 22.218 23.5 21.778 23.74C21.3446 23.9733 20.858 24.09 20.318 24.09ZM20.318 22.87C20.5713 22.87 20.808 22.81 21.028 22.69C21.2546 22.5633 21.4346 22.3767 21.568 22.13C21.7013 21.8833 21.768 21.5833 21.768 21.23C21.768 20.7033 21.628 20.3 21.348 20.02C21.0746 19.7333 20.738 19.59 20.338 19.59C19.938 19.59 19.6013 19.7333 19.328 20.02C19.0613 20.3 18.928 20.7033 18.928 21.23C18.928 21.7567 19.058 22.1633 19.318 22.45C19.5846 22.73 19.918 22.87 20.318 22.87ZM25.8649 19.61V22.29C25.8649 22.4767 25.9083 22.6133 25.9949 22.7C26.0883 22.78 26.2416 22.82 26.4549 22.82H27.1049V24H26.2249C25.0449 24 24.4549 23.4267 24.4549 22.28V19.61H23.7949V18.46H24.4549V17.09H25.8649V18.46H27.1049V19.61H25.8649Z" fill="white"/>
                        </svg>`,
                    instruction: 'If there is only one lot, continue. If there are multiple lots, select the ones you want to include in the form.'
                },
                {
                    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
                        <rect width="40" height="40" rx="4" fill="#2A439F"/>
                        <rect x="9" y="9" width="22" height="22" rx="11" stroke="white" stroke-width="2"/>
                        <path d="M16.6558 17.314C16.7118 16.446 17.0431 15.7693 17.6498 15.284C18.2658 14.7987 19.0498 14.556 20.0018 14.556C20.6551 14.556 21.2198 14.6727 21.6958 14.906C22.1718 15.1393 22.5311 15.4567 22.7738 15.858C23.0164 16.2593 23.1378 16.712 23.1378 17.216C23.1378 17.7947 22.9838 18.2893 22.6758 18.7C22.3678 19.1107 21.9991 19.386 21.5698 19.526V19.582C22.1204 19.75 22.5498 20.058 22.8578 20.506C23.1658 20.9447 23.3198 21.5093 23.3198 22.2C23.3198 22.7507 23.1938 23.2407 22.9418 23.67C22.6898 24.0993 22.3164 24.44 21.8218 24.692C21.3271 24.9347 20.7344 25.056 20.0438 25.056C19.0358 25.056 18.2051 24.7993 17.5518 24.286C16.9078 23.7633 16.5624 23.0167 16.5158 22.046H18.0558C18.0931 22.5407 18.2844 22.9467 18.6298 23.264C18.9751 23.572 19.4418 23.726 20.0298 23.726C20.5991 23.726 21.0378 23.572 21.3458 23.264C21.6538 22.9467 21.8078 22.5407 21.8078 22.046C21.8078 21.3927 21.5978 20.9307 21.1778 20.66C20.7671 20.38 20.1324 20.24 19.2738 20.24H18.9098V18.924H19.2878C20.0438 18.9147 20.6178 18.7887 21.0098 18.546C21.4111 18.3033 21.6118 17.9207 21.6118 17.398C21.6118 16.95 21.4671 16.5953 21.1778 16.334C20.8884 16.0633 20.4778 15.928 19.9458 15.928C19.4231 15.928 19.0171 16.0633 18.7278 16.334C18.4384 16.5953 18.2658 16.922 18.2098 17.314H16.6558Z" fill="white"/>
                        </svg>`,
                    instruction: 'Complete all three sections (verification, count and documents) for each selected lot.'
                },
                {
                    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
                        <rect width="40" height="40" rx="4" fill="#2A439F"/>
                        <path d="M20 14V20M20 20V26M20 20H26M20 20L14 20" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>`,
                    instruction: 'Add lots from another PO if needed or confirm that all necessary lots are included.'
                },
                {
                    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
                        <rect width="40" height="40" rx="4" fill="#2A439F"/>
                        <path d="M17 20L19 22L23 18M29 20C29 24.9706 24.9706 29 20 29C15.0294 29 11 24.9706 11 20C11 15.0294 15.0294 11 20 11C24.9706 11 29 15.0294 29 20Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>`,
                    instruction: 'Submit the form!'
                },
                {
                    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
                        <rect width="40" height="40" rx="4" fill="#C24047"/>
                        <path d="M20 16V20L23 23M29 20C29 24.9706 24.9706 29 20 29C15.0294 29 11 24.9706 11 20C11 15.0294 15.0294 11 20 11C24.9706 11 29 15.0294 29 20Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>`,
                    instruction: 'Please be aware that after 2 hour of inactivity, the form will time out, and all progress will be lost.',
                    color: 'text-red-500'
                }
            ],
            password: '',
            error: '',
            isSubmitted: false,
            instructionOpen: true,
            showPassword: false,
        }
    },
    validations: {
        password: {
            required: helpers.withMessage(() => `Password is required.`, required),
        },
    },
    mounted() {
        //
    },
    methods: {
        loginUser() {
            this.error = ''
            this.isSubmitted = true
            this.v$.$touch();
            if (this.v$.$error) {
                this.isSubmitted = false
                return;
            }
            axios({
                method: 'post',
                url: `bol-manufacturers/`,
                data: {password: this.password}
            }).then(res => {
                this.$store.dispatch('setMfgSupplierData', {...res.data, storeToken: true})
                    .then(res => {
                        if (res) {
                            this.$router.replace({name: 'manufacturer_purchase_orders'})
                        } else {
                            this.$router.replace({name: 'manufacturer'});
                        }
                    })
                    .catch(err => {
                        this.error = 'Something went wrong, please try again'
                        this.$router.replace({name: 'manufacturer'});
                    })
                this.isSubmitted = false;
            }).catch(err => {
                if ('response' in err && 'detail' in err.response.data && err.response.data.detail) {
                    this.error = err.response.data.detail
                } else {
                    this.error = 'Something went wrong, please try again'
                }
                this.isSubmitted = false;
            })
        }
    }
}
</script>